import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  MenuItem
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../store';
import { updateOrgStaffRole, fetchOrgRoles } from '../../../../modules/accessControl/actions';
import { selectUpdateOrgStaffRoleLoading, selectOrgRoles } from '../../../../modules/accessControl/selectors';
import { OrgStaff } from '../../../../modules/accessControl/types';

interface Props {
  open: boolean;
  onClose: () => void;
  mapping: OrgStaff | null;
}

const UpdateStaffRoleModal: React.FC<Props> = ({ open, onClose, mapping }) => {
  const dispatch = useDispatch<AppDispatch>();
  const isUpdating = useSelector(selectUpdateOrgStaffRoleLoading);
  const roles = useSelector(selectOrgRoles);

  const [selectedRoleId, setSelectedRoleId] = useState<string>('');

  useEffect(() => {
    if (open && mapping) {
      dispatch(fetchOrgRoles());
      if (mapping.system_role) {
        setSelectedRoleId(mapping.system_role.role_id.toString());
      } else {
        setSelectedRoleId('');
      }
    }
  }, [open, mapping, dispatch]);

  const handleUpdate = () => {
    if (mapping && selectedRoleId) {
      dispatch(updateOrgStaffRole({
        mapping_id: mapping.mapping_id,
        role_id: Number(selectedRoleId)
      }));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Update Staff Role</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ py: 2 }}>
          <TextField
            fullWidth
            label="Staff Member"
            value={mapping ? `${mapping.user.first_name} ${mapping.user.last_name}` : ''}
            disabled
            size="small"
            sx={{ mb: 3 }}
          />
          <TextField
            select
            fullWidth
            label="Organization Role"
            value={selectedRoleId}
            onChange={(e) => setSelectedRoleId(e.target.value)}
            size="small"
          >
            {Array.isArray(roles) && roles.map((role) => (
              <MenuItem key={role.role_id} value={role.role_id}>
                {role.role_name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#666' }}>
          Cancel
        </Button>
        <Button
          onClick={handleUpdate}
          variant="contained"
          disabled={isUpdating || !selectedRoleId || selectedRoleId === mapping?.system_role?.role_id?.toString()}
          sx={{
            textTransform: 'none',
            backgroundColor: '#5E8E7E',
            '&:hover': { backgroundColor: '#4A7A6A' }
          }}
        >
          {isUpdating ? 'Updating...' : 'Update Role'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateStaffRoleModal;
